import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Trigger,
  Tag,
  Space,
  Empty,
  Spin,
  Typography,
  Divider,
  Tooltip,
  Message,
} from '@arco-design/web-react';
import {
  IconNotification,
  IconCheck,
  IconCheckCircle,
  IconDelete,
  IconInfoCircle,
  IconExclamationCircle,
  IconCloseCircle,
  IconSettings,
} from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';
import { useNotificationStream } from '../hooks/useNotificationStream';
import NotificationPreferences from './NotificationPreferences';
import './NotificationBell.css';

const { Text, Paragraph } = Typography;

const PRIORITY_COLORS = {
  low: 'gray',
  medium: 'blue',
  high: 'orangered',
  urgent: 'red',
};

const TYPE_ICONS = {
  system: <IconInfoCircle />,
  warning: <IconExclamationCircle style={{ color: 'var(--color-warning-6)' }} />,
  error: <IconCloseCircle style={{ color: 'var(--color-danger-6)' }} />,
  success: <IconCheckCircle style={{ color: 'var(--color-success-6)' }} />,
};

function formatTimeAgo(dateStr, t) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return t('notifications.timeAgo.justNow');
  if (diffMin < 60) return t('notifications.timeAgo.minutes', { count: diffMin });
  if (diffHours < 24) return t('notifications.timeAgo.hours', { count: diffHours });
  if (diffDays < 7) return t('notifications.timeAgo.days', { count: diffDays });
  return date.toLocaleDateString();
}

const NotificationBell = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [prefsVisible, setPrefsVisible] = useState(false);

  // SSE real-time stream — invalidates queries on new notifications
  useNotificationStream();

  // Unread count — polls as backup (SSE handles real-time)
  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS_UNREAD_COUNT);
      return res.data;
    },
    refetchInterval: 60000,
    staleTime: 15000,
  });

  // Notification list — only fetches when popover is open
  const { data: listData, isLoading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS_LIST, {}, { page_size: 15 });
      return res.data;
    },
    enabled: popoverVisible,
    staleTime: 10000,
  });

  const unreadCount = unreadData?.count ?? 0;
  const notifications = listData?.items ?? [];

  // Mark one as read
  const markReadMutation = useMutation({
    mutationFn: (notificationId) =>
      apiClient.put(API_ENDPOINTS.NOTIFICATIONS_MARK_READ, { notification_id: notificationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read
  const markAllReadMutation = useMutation({
    mutationFn: () => apiClient.put(API_ENDPOINTS.NOTIFICATIONS_MARK_ALL_READ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      Message.success(t('notifications.allMarkedRead'));
    },
  });

  // Delete
  const deleteMutation = useMutation({
    mutationFn: (notificationId) =>
      apiClient.delete(API_ENDPOINTS.NOTIFICATIONS_DELETE, { notification_id: notificationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const handleItemClick = (item) => {
    if (!item.is_read) {
      markReadMutation.mutate(item.id);
    }
  };

  const content = (
    <div className="notification-popover">
      <div className="notification-popover-header">
        <Text bold style={{ fontSize: 16 }}>{t('notifications.title')}</Text>
        <Space size={4}>
          <Tooltip content={t('notifications.preferences.title')} mini>
            <Button
              type="text"
              size="mini"
              icon={<IconSettings />}
              onClick={() => {
                setPopoverVisible(false);
                setPrefsVisible(true);
              }}
            />
          </Tooltip>
          {unreadCount > 0 && (
            <Button
              type="text"
              size="mini"
              icon={<IconCheckCircle />}
              onClick={() => markAllReadMutation.mutate()}
              loading={markAllReadMutation.isPending}
            >
              {t('notifications.markAllRead')}
            </Button>
          )}
        </Space>
      </div>
      <Divider style={{ margin: '8px 0' }} />

      {isLoading ? (
        <div className="notification-loading">
          <Spin size={24} />
        </div>
      ) : notifications.length === 0 ? (
        <Empty
          description={t('notifications.empty')}
          style={{ padding: '24px 0' }}
        />
      ) : (
        <div className="notification-list">
          {notifications.map((item) => (
            <div
              key={item.id}
              className={`notification-item ${!item.is_read ? 'notification-unread' : ''}`}
              onClick={() => handleItemClick(item)}
            >
              <div className="notification-item-icon">
                {TYPE_ICONS[item.notification_type] || TYPE_ICONS.system}
              </div>
              <div className="notification-item-content">
                <div className="notification-item-title">
                  <Text bold={!item.is_read} style={{ fontSize: 13 }}>
                    {item.title}
                  </Text>
                  {item.priority && item.priority !== 'medium' && (
                    <Tag size="small" color={PRIORITY_COLORS[item.priority]}>
                      {t(`notifications.priority.${item.priority}`)}
                    </Tag>
                  )}
                </div>
                {item.message && (
                  <Paragraph
                    style={{
                      margin: 0,
                      fontSize: 12,
                      color: 'var(--color-text-3)',
                      wordBreak: 'normal',
                      overflowWrap: 'normal',
                      whiteSpace: 'normal',
                    }}
                  >
                    {item.message}
                  </Paragraph>
                )}
                <Text style={{ fontSize: 11, color: 'var(--color-text-4)' }}>
                  {formatTimeAgo(item.created_at, t)}
                </Text>
              </div>
              <div className="notification-item-actions">
                <Tooltip content={t('common.actions.delete')} mini>
                  <Button
                    type="text"
                    size="mini"
                    icon={<IconDelete />}
                    status="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.mutate(item.id);
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Trigger
        trigger="click"
        position="bottom"
        popupVisible={popoverVisible}
        onVisibleChange={setPopoverVisible}
        popup={() => content}
        unmountOnExit={false}
        getPopupContainer={() => document.body}
        popupAlign={{ bottom: 8 }}
        className="notification-trigger"
      >
        <Badge count={unreadCount} maxCount={99} dotStyle={{ fontSize: 10 }}>
          <Button
            type="text"
            icon={<IconNotification />}
          />
        </Badge>
      </Trigger>
      <NotificationPreferences
        visible={prefsVisible}
        onClose={() => setPrefsVisible(false)}
      />
    </>
  );
};

export default NotificationBell;
